import { useEffect, useState } from "react";
import axios from "axios";
import {
  Loader2, Plus, Pencil, Trash2, Upload, LogOut, FileCheck2, FileX2, X,
  BadgeCheck, Ban, KeyRound,
} from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { Seo } from "@/components/Seo";
import { API, imgUrl } from "@/lib/api";

const CATS = [
  ["numbers", "Number Worksheets"],
  ["alphabet", "Alphabet Worksheets"],
  ["counting", "Counting Worksheets"],
  ["tracing", "Tracing Worksheets"],
  ["workbooks", "Preschool Workbooks"],
  ["coloring", "Coloring & Activity Sheets"],
  ["math", "Preschool Math"],
  ["shapes", "Shapes"],
  ["colors", "Colors"],
  ["phonics", "Phonics"],
  ["bundles", "Preschool Bundles"],
];

const EMPTY = {
  name: "", tagline: "", description: "", long_description: "", price: 49,
  ages: "3-6 years", pages: 0, category: "workbooks", kind: "workbook",
  cover_image: "", accent: "#3B82F6", learn: "", included: "",
  is_featured: false, is_new: true, available: true, payment_link: "",
};

const inputCls =
  "w-full rounded-xl border-2 border-slate-200 bg-white px-4 py-3 font-semibold text-slate-800 outline-none focus:border-sky-400";

const STATUS_STYLES = {
  awaiting_payment: "bg-slate-100 text-slate-500",
  pending_verification: "bg-amber-100 text-amber-700",
  paid: "bg-green-100 text-green-700",
  rejected: "bg-red-100 text-red-600",
};

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem("dkl_admin_token") || "");
  const [login, setLogin] = useState({ email: "", password: "" });
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("orders");
  const [form, setForm] = useState(null);
  const [editSlug, setEditSlug] = useState("");
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = () => {
    axios.get(`${API}/admin/products`, auth).then((r) => setProducts(r.data.products)).catch(handle401);
    axios.get(`${API}/admin/orders`, auth).then((r) => setOrders(r.data.orders)).catch(handle401);
  };

  const handle401 = (e) => {
    if (e.response?.status === 401) logout();
  };

  useEffect(() => {
    if (token) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const logout = () => {
    localStorage.removeItem("dkl_admin_token");
    setToken("");
  };

  const doLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, login);
      localStorage.setItem("dkl_admin_token", data.token);
      setToken(data.token);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/admin/change-password`, pw, auth);
      toast.success("Password updated");
      setPw({ current_password: "", new_password: "" });
      setShowPw(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update password");
    } finally {
      setBusy(false);
    }
  };

  const approveOrder = async (orderId) => {
    try {
      await axios.post(`${API}/admin/orders/${orderId}/approve`, {}, auth);
      toast.success("Payment verified — download unlocked and email sent");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Approve failed");
    }
  };

  const rejectOrder = async (orderId) => {
    if (!window.confirm("Reject this order? The customer will not receive the download.")) return;
    try {
      await axios.post(`${API}/admin/orders/${orderId}/reject`, {}, auth);
      toast.success("Order rejected");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reject failed");
    }
  };

  const openEdit = (p) => {
    setEditSlug(p.slug);
    setForm({ ...EMPTY, ...p, learn: (p.learn || []).join("\n"), included: (p.included || []).join("\n") });
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = {
      ...form,
      price: Number(form.price),
      pages: Number(form.pages),
      learn: form.learn.split("\n").map((s) => s.trim()).filter(Boolean),
      included: form.included.split("\n").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (editSlug) {
        await axios.put(`${API}/admin/products/${editSlug}`, payload, auth);
        toast.success("Product updated");
      } else {
        await axios.post(`${API}/admin/products`, payload, auth);
        toast.success("Product created");
      }
      setForm(null);
      setEditSlug("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (slug) => {
    if (!window.confirm(`Delete "${slug}" permanently?`)) return;
    await axios.delete(`${API}/admin/products/${slug}`, auth);
    toast.success("Product deleted");
    load();
  };

  const uploadPdf = async (slug, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      await axios.post(`${API}/admin/products/${slug}/pdf`, fd, auth);
      toast.success("PDF uploaded — previews generated");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "PDF upload failed");
    } finally {
      setBusy(false);
    }
  };

  const uploadCover = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/admin/upload`, fd, auth);
      setForm((f) => ({ ...f, cover_image: data.url }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Image upload failed");
    }
  };

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-sky-soft px-4 font-body">
        <Seo title="Admin Login | Divine Kids Learning" description="Store admin login." />
        <form onSubmit={doLogin} className="w-full max-w-md rounded-[2rem] bg-white p-8 shadow-xl shadow-blue-900/5">
          <Logo className="text-lg" />
          <h1 className="mt-4 font-display text-2xl font-bold text-slate-800">Admin Login</h1>
          <div className="mt-6 space-y-4">
            <input data-testid="admin-email-input" required type="email" placeholder="Admin email" className={inputCls}
              value={login.email} onChange={(e) => setLogin({ ...login, email: e.target.value })} />
            <input data-testid="admin-password-input" required type="password" placeholder="Password" className={inputCls}
              value={login.password} onChange={(e) => setLogin({ ...login, password: e.target.value })} />
            <button data-testid="admin-login-button" type="submit" disabled={busy}
              className="h-12 w-full rounded-full bg-brand-blue font-display text-lg font-bold text-white shadow-[0_4px_0_0_#1D4ED8] active:translate-y-1 active:shadow-none disabled:opacity-60">
              {busy ? <Loader2 className="mx-auto h-5 w-5 animate-spin" /> : "Log In"}
            </button>
          </div>
        </form>
      </div>
    );
  }

  const pending = orders.filter((o) => o.status === "pending_verification");

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-sky-soft px-4 py-10 font-body sm:px-8">
      <Seo title="Admin | Divine Kids Learning" description="Manage store products and orders." />
      <div className="mx-auto max-w-5xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <Logo className="text-lg" />
            <h1 className="mt-2 font-display text-3xl font-bold text-slate-800">Store Manager</h1>
          </div>
          <div className="flex flex-wrap gap-3">
            <button data-testid="change-password-button" onClick={() => setShowPw(!showPw)}
              className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-3 font-display text-base font-bold text-slate-600 shadow-sm">
              <KeyRound className="h-4 w-4" /> Password
            </button>
            <button data-testid="admin-logout-button" onClick={logout}
              className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-3 font-display text-base font-bold text-slate-600 shadow-sm">
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>

        {showPw && (
          <form data-testid="password-form" onSubmit={changePassword} className="mt-6 rounded-[1.5rem] bg-white p-6 shadow-lg shadow-blue-900/5">
            <h2 className="font-display text-lg font-bold text-slate-800">Change Password</h2>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <input data-testid="current-password-input" required type="password" placeholder="Current password" className={inputCls}
                value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} />
              <input data-testid="new-password-input" required minLength={8} type="password" placeholder="New password (min 8 chars)" className={inputCls}
                value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} />
            </div>
            <button data-testid="save-password-button" type="submit" disabled={busy}
              className="mt-4 h-11 rounded-full bg-brand-blue px-8 font-display text-base font-bold text-white shadow-[0_4px_0_0_#1D4ED8] active:translate-y-1 active:shadow-none disabled:opacity-60">
              {busy ? <Loader2 className="mx-auto h-5 w-5 animate-spin" /> : "Update Password"}
            </button>
          </form>
        )}

        <div className="mt-8 flex gap-2">
          {["orders", "products"].map((t) => (
            <button key={t} data-testid={`tab-${t}`} onClick={() => setTab(t)}
              className={`rounded-full px-6 py-2.5 font-display text-base font-bold capitalize ${
                tab === t ? "bg-slate-800 text-white" : "bg-white text-slate-600"
              }`}>
              {t}{t === "orders" && pending.length > 0 ? ` (${pending.length})` : ""}
            </button>
          ))}
        </div>

        {tab === "orders" && (
          <div className="mt-6 space-y-4" data-testid="admin-order-list">
            {orders.length === 0 && (
              <p className="rounded-[1.5rem] bg-white p-8 text-center font-semibold text-slate-500">No orders yet.</p>
            )}
            {orders.map((o) => (
              <div key={o.order_id} data-testid={`admin-order-${o.order_id.slice(0, 8)}`}
                className="rounded-[1.5rem] bg-white p-5 shadow-lg shadow-blue-900/5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="font-display text-lg font-bold text-slate-800">
                      {o.name} · ₹{o.amount} · {o.product_slug}
                    </p>
                    <p className="text-sm font-semibold text-slate-500">
                      {o.email} {o.phone ? `· ${o.phone}` : ""} · {new Date(o.created_at).toLocaleString("en-IN")}
                    </p>
                    {o.txn_ref && (
                      <p className="mt-1 text-sm font-bold text-slate-600">
                        UTR: {o.txn_ref} {o.upi_id ? `· from ${o.upi_id}` : ""}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`rounded-full px-3 py-1.5 text-xs font-bold capitalize ${STATUS_STYLES[o.status] || "bg-slate-100 text-slate-500"}`}>
                      {o.status.replace(/_/g, " ")}
                    </span>
                    {o.status === "pending_verification" && (
                      <>
                        <button data-testid={`approve-${o.order_id.slice(0, 8)}`} onClick={() => approveOrder(o.order_id)}
                          className="inline-flex items-center gap-1.5 rounded-full bg-brand-green px-4 py-2 font-display text-sm font-bold text-white shadow-[0_3px_0_0_#15803D] active:translate-y-0.5 active:shadow-none">
                          <BadgeCheck className="h-4 w-4" /> Verify & Deliver
                        </button>
                        <button data-testid={`reject-${o.order_id.slice(0, 8)}`} onClick={() => rejectOrder(o.order_id)}
                          className="inline-flex items-center gap-1.5 rounded-full bg-red-100 px-4 py-2 font-display text-sm font-bold text-brand-red">
                          <Ban className="h-4 w-4" /> Reject
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "products" && (
          <>
            <div className="mt-6 flex justify-end">
              <button data-testid="add-product-button" onClick={() => { setEditSlug(""); setForm({ ...EMPTY }); }}
                className="inline-flex items-center gap-2 rounded-full bg-brand-green px-6 py-3 font-display text-base font-bold text-white shadow-[0_4px_0_0_#15803D] active:translate-y-1 active:shadow-none">
                <Plus className="h-5 w-5" /> Add Product
              </button>
            </div>

            {form && (
              <form data-testid="product-form" onSubmit={save} className="mt-4 rounded-[2rem] bg-white p-7 shadow-xl shadow-blue-900/5">
                <div className="flex items-center justify-between">
                  <h2 className="font-display text-xl font-bold text-slate-800">{editSlug ? `Edit: ${editSlug}` : "New Product"}</h2>
                  <button type="button" onClick={() => { setForm(null); setEditSlug(""); }} aria-label="Close form">
                    <X className="h-6 w-6 text-slate-400" />
                  </button>
                </div>
                <div className="mt-5 grid gap-4 sm:grid-cols-2">
                  <input data-testid="form-name" required placeholder="Product title" className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                  <input data-testid="form-tagline" placeholder="Tagline (e.g. Trace, Count, Colour)" className={inputCls} value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} />
                  <input data-testid="form-price" required type="number" min="1" placeholder="Price (INR)" className={inputCls} value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
                  <input data-testid="form-pages" type="number" min="0" placeholder="Number of pages" className={inputCls} value={form.pages} onChange={(e) => setForm({ ...form, pages: e.target.value })} />
                  <select data-testid="form-category" className={inputCls} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                    {CATS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                  <select data-testid="form-kind" className={inputCls} value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
                    <option value="workbook">Workbook</option>
                    <option value="worksheet">Worksheet</option>
                  </select>
                  <input data-testid="form-ages" placeholder="Age range" className={inputCls} value={form.ages} onChange={(e) => setForm({ ...form, ages: e.target.value })} />
                  <input data-testid="form-payment-link" placeholder="Payment link (optional — e.g. SuperProfile page)" className={inputCls} value={form.payment_link || ""} onChange={(e) => setForm({ ...form, payment_link: e.target.value })} />
                  <div className="flex items-center gap-3">
                    <input data-testid="form-cover" placeholder="Cover image URL" className={inputCls} value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} />
                    <label className="flex shrink-0 cursor-pointer items-center gap-1 rounded-full bg-slate-100 px-4 py-3 text-sm font-bold text-slate-600">
                      <Upload className="h-4 w-4" />
                      <input data-testid="form-cover-upload" type="file" accept="image/*" className="hidden" onChange={(e) => uploadCover(e.target.files[0])} />
                    </label>
                  </div>
                </div>
                <textarea data-testid="form-description" placeholder="Short description" rows={2} className={`${inputCls} mt-4`} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                <textarea data-testid="form-long-description" placeholder="Full product description" rows={3} className={`${inputCls} mt-4`} value={form.long_description} onChange={(e) => setForm({ ...form, long_description: e.target.value })} />
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <textarea data-testid="form-learn" placeholder="What children will learn (one per line)" rows={3} className={inputCls} value={form.learn} onChange={(e) => setForm({ ...form, learn: e.target.value })} />
                  <textarea data-testid="form-included" placeholder="What's included (one per line)" rows={3} className={inputCls} value={form.included} onChange={(e) => setForm({ ...form, included: e.target.value })} />
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-6">
                  <label className="flex items-center gap-2 font-semibold text-slate-600">
                    <input data-testid="form-available" type="checkbox" checked={form.available} onChange={(e) => setForm({ ...form, available: e.target.checked })} className="h-5 w-5" /> Available for sale
                  </label>
                  <label className="flex items-center gap-2 font-semibold text-slate-600">
                    <input data-testid="form-featured" type="checkbox" checked={form.is_featured} onChange={(e) => setForm({ ...form, is_featured: e.target.checked })} className="h-5 w-5" /> Featured
                  </label>
                  <label className="flex items-center gap-2 font-semibold text-slate-600">
                    <input data-testid="form-new" type="checkbox" checked={form.is_new} onChange={(e) => setForm({ ...form, is_new: e.target.checked })} className="h-5 w-5" /> New
                  </label>
                  <input data-testid="form-accent" type="color" value={form.accent} onChange={(e) => setForm({ ...form, accent: e.target.value })} className="h-10 w-16 cursor-pointer rounded-lg" title="Accent color" />
                </div>
                <button data-testid="save-product-button" type="submit" disabled={busy}
                  className="mt-6 h-12 w-full rounded-full bg-brand-blue font-display text-lg font-bold text-white shadow-[0_4px_0_0_#1D4ED8] active:translate-y-1 active:shadow-none disabled:opacity-60">
                  {busy ? <Loader2 className="mx-auto h-5 w-5 animate-spin" /> : editSlug ? "Save Changes" : "Create Product"}
                </button>
              </form>
            )}

            <div className="mt-6 space-y-4" data-testid="admin-product-list">
              {products.map((p) => (
                <div key={p.slug} data-testid={`admin-product-${p.slug}`} className="flex flex-wrap items-center gap-4 rounded-[1.5rem] bg-white p-5 shadow-lg shadow-blue-900/5">
                  {p.cover_image && <img src={imgUrl(p.cover_image)} alt={p.name} className="h-16 w-16 rounded-xl object-cover" />}
                  <div className="min-w-0 flex-1">
                    <p className="font-display text-lg font-bold text-slate-800">{p.name}</p>
                    <p className="text-sm font-semibold text-slate-500">
                      ₹{p.price} · {p.category} · {p.kind} · {p.pages} pages
                      {p.available === false && " · coming soon"}
                    </p>
                  </div>
                  <span className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold ${p.pdf_path ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"}`}>
                    {p.pdf_path ? <FileCheck2 className="h-4 w-4" /> : <FileX2 className="h-4 w-4" />}
                    {p.pdf_path ? "PDF ready" : "No PDF"}
                  </span>
                  <label className="flex cursor-pointer items-center gap-1.5 rounded-full bg-slate-100 px-4 py-2 text-sm font-bold text-slate-600 hover:bg-slate-200">
                    <Upload className="h-4 w-4" /> PDF
                    <input data-testid={`pdf-upload-${p.slug}`} type="file" accept="application/pdf" className="hidden" onChange={(e) => uploadPdf(p.slug, e.target.files[0])} />
                  </label>
                  <button data-testid={`edit-${p.slug}`} onClick={() => openEdit(p)} className="rounded-full bg-sky-100 p-2.5 text-brand-blue" aria-label={`Edit ${p.name}`}>
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button data-testid={`delete-${p.slug}`} onClick={() => remove(p.slug)} className="rounded-full bg-red-100 p-2.5 text-brand-red" aria-label={`Delete ${p.name}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
